import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import OpenPlaysLink from "@/components/OpenPlaysLink";
import axios from "axios";

import { API } from "@/lib/apiBase";

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const pendingBookingId = searchParams.get("booking_id");
  const pendingGroupId = searchParams.get("group_id");
  const isPending = searchParams.get("pending") === "true";
  
  const [status, setStatus] = useState("checking");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentDetails(response.data);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch {
      setStatus("error");
    }
  }, [attempts, sessionId]);

  useEffect(() => {
    if (isPending) {
      setStatus("pending_confirmation");
      return;
    }
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId, attempts, pollPaymentStatus, isPending]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </h1>
          <OpenPlaysLink />
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 section-padding">
        <div className="max-w-2xl mx-auto">
          <div className="card-surface p-12 text-center">
            {status === "checking" && (
              <div data-testid="payment-checking">
                <Loader2 className="w-16 h-16 text-[#8cc63f] mx-auto mb-6 animate-spin" />
                <h1 className="text-3xl font-bold mb-4">Checking Payment Status...</h1>
                <p className="text-[#a1a1aa]">
                  Please wait while we confirm your payment.
                </p>
              </div>
            )}

            {status === "pending_confirmation" && (
              <div data-testid="payment-pending-confirmation">
                <Clock className="w-16 h-16 text-[#8cc63f] mx-auto mb-6" />
                <p className="text-[#e4e4e7] mb-6 leading-relaxed text-base">
                  Your receipt has been received. Please check your email for
                  updates regarding your booking confirmation, and don&rsquo;t
                  forget to check your spam or junk folder as well. If you have
                  any concerns, feel free to contact us through our social media
                  pages.
                </p>
                {pendingBookingId && (
                  <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-8 inline-block">
                    <p className="label-text mb-1">Booking Reference</p>
                    <p className="font-mono text-sm text-white">
                      {pendingBookingId.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                )}
                {pendingGroupId && !pendingBookingId && (
                  <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-8 inline-block">
                    <p className="label-text mb-1">Group Reference</p>
                    <p
                      data-testid="pending-group-ref"
                      className="font-mono text-sm text-white"
                    >
                      {pendingGroupId.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                )}
                <div className="flex gap-4 justify-center flex-wrap">
                  <button
                    data-testid="pending-book-another-button"
                    onClick={() => navigate("/availability")}
                    className="btn-primary"
                  >
                    Book Another Court
                  </button>
                  <button
                    data-testid="pending-home-button"
                    onClick={() => navigate("/")}
                    className="btn-secondary"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}

            {status === "success" && (
              <div data-testid="payment-success">
                <CheckCircle2 className="w-16 h-16 text-[#8cc63f] mx-auto mb-6" />
                <h1 className="text-3xl font-bold mb-4 text-[#8cc63f]">
                  Booking Confirmed!
                </h1>
                <p className="text-[#a1a1aa] mb-8">
                  Your payment was successful. You'll receive a confirmation email shortly.
                </p>
                {paymentDetails && (
                  <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 mb-8 text-left">
                    <div className="flex justify-between mb-3">
                      <span className="text-[#a1a1aa]">Amount Paid:</span>
                      <span className="font-bold">
                        ₱{(paymentDetails.amount_total / 100).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#a1a1aa]">Payment Status:</span>
                      <span className="font-bold text-[#8cc63f]">
                        {paymentDetails.payment_status}
                      </span>
                    </div>
                  </div>
                )}
                <div className="flex gap-4 justify-center">
                  <button
                    data-testid="book-another-button"
                    onClick={() => navigate("/courts")}
                    className="btn-primary"
                  >
                    Book Another Court
                  </button>
                  <button
                    data-testid="home-button"
                    onClick={() => navigate("/")}
                    className="btn-secondary"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}

            {(status === "error" || status === "expired" || status === "timeout") && (
              <div data-testid="payment-error">
                <XCircle className="w-16 h-16 text-[#ff3b30] mx-auto mb-6" />
                <h1 className="text-3xl font-bold mb-4 text-[#ff3b30]">
                  {status === "timeout" ? "Payment Verification Timeout" : "Payment Failed"}
                </h1>
                <p className="text-[#a1a1aa] mb-8">
                  {status === "timeout"
                    ? "We couldn't verify your payment status. Please check your email for confirmation or contact support."
                    : "There was an issue with your payment. Please try again or contact support."}
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    data-testid="try-again-button"
                    onClick={() => navigate("/courts")}
                    className="btn-primary"
                  >
                    Try Again
                  </button>
                  <button
                    data-testid="home-button-error"
                    onClick={() => navigate("/")}
                    className="btn-secondary"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingConfirmation;
